import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { MapView } from "../components/MapView";
import { toast } from "sonner";
import { MapPin, Clock, CalendarBlank, ArrowLeft, ChatCircle, Star, LockKey, LockKeyOpen, Lightning } from "@phosphor-icons/react";
import { secLabelKey } from "../lib/securityTypes";

export default function JobDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [job, setJob] = useState(null);
  const [applied, setApplied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);

  const load = () => api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => navigate("/jobs"));
  useEffect(() => { load(); }, [id]);

  // After returning from Stripe unlock checkout
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/subscription/status/${sid}`);
        if (data.payment_status === "paid") {
          setPolling(false); setParams({});
          if (data.purpose === "spoed_claim") {
            const r = await api.get(`/jobs/${id}`);
            setJob(r.data);
            if (r.data.unlocked) toast.success(t("spoed_claimed_success"));
            else toast.info(t("spoed_refunded"));
          } else {
            toast.success(t("unlock_success")); load();
          }
          return;
        }
        if (data.status === "expired" || attempts >= 6) { setPolling(false); setParams({}); return; }
      } catch (e) { if (attempts >= 6) { setPolling(false); setParams({}); return; } }
      setTimeout(poll, 2000);
    };
    poll();
  }, [params]);

  const unlock = async () => {
    if (!user) { navigate("/login"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${id}/unlock/checkout`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setBusy(false); }
  };

  const claim = async () => {
    if (!user) { navigate("/login"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${id}/claim/checkout`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setBusy(false); }
  };

  const apply = async () => {
    if (!user) { navigate("/login"); return; }
    setBusy(true);
    try {
      await api.post("/applications", { job_id: id, message: "" });
      setApplied(true);
      toast.success(t("applied"));
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!job) return <div className="max-w-4xl mx-auto px-5 py-20 text-center text-zinc-500">{t("loading")}</div>;
  const locked = job.locked;
  const isSpoed = !!job.is_urgent;
  const spoedTaken = isSpoed && job.status !== "open" && !job.unlocked;

  return (
    <div className="max-w-5xl mx-auto px-5 py-8">
      <button data-testid="back-btn" onClick={() => navigate("/jobs")} className="flex items-center gap-1 text-sm text-zinc-500 hover:text-[#E11D2A] transition-colors mb-6"><ArrowLeft size={16} /> {t("back")}</button>

      {polling && <div data-testid="unlock-processing" className="mb-4 text-center bg-amber-50 text-amber-700 border border-amber-200 rounded-md py-3">{t("unlocking")}</div>}

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          <div className="bg-white border border-zinc-200 rounded-md p-6">
            <div className="flex items-center gap-2 flex-wrap">
              {isSpoed && <span data-testid="spoed-badge" className="text-xs px-2 py-0.5 rounded-full font-bold bg-[#E11D2A] text-white flex items-center gap-1"><Lightning size={12} weight="fill" />{t("spoed_badge")}</span>}
              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-[#E11D2A]/10 text-[#E11D2A]">{job.security_type ? t(secLabelKey(job.security_type)) : t("sec_generic")}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${job.job_type === "long_job" ? "bg-zinc-100 text-zinc-600" : "bg-[#FFCC00]/20 text-[#8a6d00]"}`}>
                {job.job_type === "long_job" ? t("type_long") : t("type_short")}
              </span>
              {job.unlocked && <span data-testid="unlocked-badge" className="text-xs px-2 py-0.5 rounded-full font-semibold bg-green-100 text-green-700 flex items-center gap-1"><LockKeyOpen size={12} weight="fill" />{t("unlocked_badge")}</span>}
            </div>
            <h1 className="font-head text-3xl font-bold tracking-tight mt-3">{job.title || (job.security_type ? t(secLabelKey(job.security_type)) : t("sec_generic"))}</h1>
            {job.company_name && (
              <button data-testid="company-link" onClick={() => navigate(`/profile/${job.company_id}`)} className="text-sm text-[#E11D2A] font-semibold mt-1 flex items-center gap-1">
                {job.company_name}
                {job.company?.rating > 0 && <span className="flex items-center gap-0.5 text-zinc-500"><Star size={13} weight="fill" className="text-[#FFCC00]" />{job.company.rating}</span>}
              </button>
            )}
            <div className="mt-5 grid sm:grid-cols-3 gap-4 text-sm">
              <Info icon={<MapPin size={18} />} label={t("location")} value={`${job.location_name}${job.country ? ", " + job.country : ""}`} />
              <Info icon={<CalendarBlank size={18} />} label={t("date")} value={`${job.date} ${job.start_time || ""}`} />
              <Info icon={<Clock size={18} />} label={t("duration")} value={`${job.duration_hours} ${t("hours")}`} />
            </div>
            {job.guards_needed > 1 && (
              <div data-testid="job-slots" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#E11D2A] bg-[#E11D2A]/10 rounded-full px-3 py-1">
                {(job.guards_filled || 0)}/{job.guards_needed} {t("guards_slots")}
              </div>
            )}
            {locked ? (
              <div data-testid="locked-desc" className="mt-5 flex items-center gap-2 text-sm text-zinc-500 bg-zinc-50 border border-dashed border-zinc-200 rounded-md p-4">
                <LockKey size={18} className="text-zinc-400 shrink-0" /> {t("locked_desc")}
              </div>
            ) : (
              job.description && <p className="mt-5 text-zinc-700 whitespace-pre-line">{job.description}</p>
            )}
          </div>
          <div className="h-72"><MapView markers={[job]} zoom={12} /></div>
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-zinc-200 rounded-md p-6 md:sticky md:top-20">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{t("you_earn")}</div>
            <div className="font-head font-black text-4xl text-[#0A0A0A] tabular-nums mt-1">€{job.pay}</div>
            <div className="text-sm text-zinc-500 mt-2">{t("access_fee_note")}</div>

            {spoedTaken ? (
              <div data-testid="spoed-unavailable" className="mt-5 text-center text-sm font-semibold text-[#E11D2A] bg-red-50 border border-red-200 rounded-md py-3">{t("spoed_unavailable")}</div>
            ) : locked ? (
              <div className="mt-5 border-t border-zinc-100 pt-4">
                <p className="text-sm text-zinc-600 mb-1">{isSpoed ? t("spoed_claim_note") : t("unlock_note")}</p>
                <p className="text-xs text-zinc-400 mb-3">€{Number(job.unlock_fee ?? 20).toFixed(2).replace(".", ",")} · {t("excl_vat")}</p>
                {isSpoed ? (
                  <button data-testid="claim-btn" disabled={busy} onClick={claim} className="w-full bg-[#E11D2A] text-white hover:bg-[#B0121F] font-bold rounded-md py-3 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                    <Lightning size={18} weight="fill" /> {user ? t("spoed_claim_cta") : t("login_to_unlock")}
                  </button>
                ) : (
                  <button data-testid="unlock-btn" disabled={busy} onClick={unlock} className="w-full bg-[#FFCC00] text-[#0A0A0A] hover:bg-[#E6B800] font-bold rounded-md py-3 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                    <LockKey size={18} weight="fill" /> {user ? t("unlock_cta") : t("login_to_unlock")}
                  </button>
                )}
              </div>
            ) : (
              <>
                {(!user || user.role === "guard") && job.status === "open" && (
                  <button data-testid="apply-btn" disabled={busy || applied} onClick={apply} className="w-full mt-5 bg-[#E11D2A] text-white hover:bg-[#B0121F] font-bold rounded-md py-3 transition-colors disabled:opacity-60">
                    {applied ? t("applied") : t("apply")}
                  </button>
                )}
                {job.status !== "open" && <div className="mt-5 text-center text-sm font-semibold text-zinc-500 bg-zinc-100 rounded-md py-3">{t("assigned")}</div>}
                {user && user.role === "guard" && (
                  <button data-testid="contact-btn" onClick={() => navigate(`/messages?job=${job.id}&user=${job.company_id}`)} className="w-full mt-3 border border-zinc-300 hover:bg-zinc-50 font-semibold rounded-md py-2.5 transition-colors flex items-center justify-center gap-2">
                    <ChatCircle size={18} /> {t("contact")}
                  </button>
                )}
              </>
            )}
          </div>
          {(!user || user.role === "guard") && (
            <div data-testid="accept-payment-note" className="bg-white border border-zinc-200 rounded-md p-4 text-xs text-zinc-500 leading-relaxed">
              <span className="font-semibold text-[#0A0A0A]">Belangrijk:</span> Door deze opdracht te accepteren ga je ermee akkoord dat de vergoeding voor de uitgevoerde werkzaamheden rechtstreeks door het opdrachtgevende bedrijf wordt betaald. GuardLink verwerkt of beheert deze betaling niet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Info = ({ icon, label, value }) => (
  <div className="flex items-start gap-2">
    <span className="text-[#E11D2A] mt-0.5">{icon}</span>
    <div><div className="text-xs uppercase tracking-wider text-zinc-400">{label}</div><div className="font-medium">{value}</div></div>
  </div>
);
